// Globals
import api from '../../api'
import enums from '../../enums.js'
import { LOCALSTORAGE_KEY_LABOREDITOR } from '../../settings.js'

export const namespaced = true

export const state = {
  list: {
    hasNext: false,
    headers: [],
    items: []
  },
  listCategories: [],
  listState: enums.status.IDLE,
  listFilter: {
    offset: 0,
    search: '',
    pubstate: '',
    category: '',
    internal: false,
    ...JSON.parse(localStorage.getItem(LOCALSTORAGE_KEY_LABOREDITOR))
  },
  deletedListFilter: {
    offset: 0
  },
  deletedAnalysis: {
    hasNext: false,
    results: {
      "name": "",
      "history": [],
    }
  },
  deletedCollectionItem: {
    hasNext: false,
    results: {
      "name": "",
      "history": [],
    }
  },
  historyListFilter: {
    offset: 0,
  },
  historicalAnalysis: {
    hasNext: false,
    results: {
      "name": "",
      "history": [],
    }
  },
  historicalCollectionItem: {
    hasNext: false,
    results: {
      "name": "",
      "history": [],
    }
  }
}

/**
 * @type {import("vuex").MutationTree<state>}
 */
export const mutations = {
  /**
   * Set analysis list
   * @param {Object} data Analysis list data
   * @param {boolean} data.has_next Has more items after offset
   * @param {string[]} data.headers Value headers
   * @param {{values: string[], identifier: string}[]} data.items Analysis list
   */
  list(state, data) {
    state.list.hasNext = data.has_next
    state.list.headers = data.headers
    state.list.items = data.items
  },

  /**
   * Set list categories
   * @param {number} data List categories
   */
  listCategories(state, data) {
    state.listCategories = data
  },

  /**
   * Set list state
   * @param {number} status List state
   */
  listState(state, status) {
    state.listState = status
  },

  /**
   * Set list filter
   * @param {Object} filter Filter settings
   */
  listFilter(state, filter) {
    // Only update filter settings that are committed and keep all the other ones.
    for (const item in filter) {
      state.listFilter[item] = filter[item]
    }

    localStorage.setItem(LOCALSTORAGE_KEY_LABOREDITOR, JSON.stringify(state.listFilter))
  },


  /**
   * Set deleted list state
   * @param {number} status List state
   */
  deletedListState(state, status) {
    state.deletedListState = status
  },

  /**
   * Set deleted list filter
   * @param {Object} filter Filter settings
   */
  deletedListFilter(state, offset) {
    // Only update filter settings that are committed and keep all the other ones.
    state.deletedListFilter.offset = offset
  },

  /**
   * Set history list filter
   * @param {Object} filter Filter settings
   */
  historyListFilter(state, offset) {
    // Only update filter settings that are committed and keep all the other ones.
    state.historyListFilter.offset = offset
  },

  /**
   * Set history items
   * @param {Array} data History items
   */
  historyAnalysis(state, data) {
    state.historicalAnalysis.hasNext = data.has_next
    state.historicalAnalysis.results = data.results
  },

  /**
   * Set collection items
   * @param {Array} data History items
   */
  historyCollectionItem(state, data) {
    state.historicalCollectionItem.hasNext = data.has_next
    state.historicalCollectionItem.results = data.results
  },

  /**
   * Set deleted analysis list
   * @param {Array} data Deleted analysis items
   */
  deletedAnalysis(state, data) {
    state.deletedAnalysis.hasNext = data.has_next
    state.deletedAnalysis.results = data.results
  },

  /**
   * Set deleted collection items list
   * @param {Array} data Deleted collection items
   */
  deletedCollectionItems(state, data) {
    state.deletedCollectionItem.hasNext = data.has_next
    state.deletedCollectionItem.results = data.results
  }
}

/**
 * @type {import("vuex").ActionTree<state>}
 */
export const actions = {
  async updateList(context, filter) {
    const oldFilter = { ...context.state.listFilter }
    context.commit('listState', enums.status.LOADING)
    context.commit('listFilter', filter)

    try {
      await api.laborEditor.list.get(context.state.listFilter)
      await api.laborEditor.category.getListWithCommit()
      context.commit('listState', enums.status.SUCCESS)
    } catch {
      // Roll back filter on unsuccessful list update.
      context.commit('listFilter', oldFilter)
      context.commit('listState', enums.status.ERROR)
    }
  },

  async updateHistoryList(context, { model, id, offset = state.historyListFilter.offset, deleted = false, collection }) {
    if (!id) {
      return
    }

    context.commit('listState', enums.status.LOADING)
    context.commit('historyListFilter', offset)
    const getHistoryItems = deleted ? api.laborEditor[model].historyApi.getDeletedHistoryItems : api.laborEditor[model].historyApi.getHistoryItems

    try {
      await getHistoryItems(id, { offset, collection })
      context.commit('listState', enums.status.SUCCESS)
    } catch {
      // Roll back filter on unsuccessful list update.
      context.commit('listState', enums.status.ERROR)
    }
  },

  async updateDeletedHistoryList(context, { model, id, offset = 0, collection }) {
    context.commit('listState', enums.status.LOADING)
    context.commit('deletedListFilter', offset)

    try {
      await api.laborEditor[model].historyApi.getDeletedItems({ offset, collection })
      context.commit('listState', enums.status.SUCCESS)
    } catch {
      // Roll back filter on unsuccessful list updte.
      context.commit('listState', enums.status.ERROR)
    }
  }
}
